import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  PieChart as PieChartIcon,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { format, subDays, startOfWeek, endOfWeek, eachDayOfInterval, parseISO, isWithinInterval } from "date-fns";

const COLORS = ['#3B82F6', '#FBBF24', '#F87171', '#2DD4BF', '#A855F7', '#EC4899', '#10B981', '#6366F1'];

const SpendingInsights = ({ expenses = [], income_range = "" }) => {
  const [timeRange, setTimeRange] = useState("week"); // week, month, all
  const [chartType, setChartType] = useState("trend"); // trend, category, dayOfWeek

  // Parse income range to get midpoint for budget comparison
  const getIncomeEstimate = () => {
    const ranges = {
      "0-2000": 1000,
      "2000-5000": 3500,
      "5000-10000": 7500,
      "10000-20000": 15000,
      "20000+": 25000
    };
    return ranges[income_range] || 5000;
  };

  // Filter expenses by time range
  const getFilteredExpenses = () => {
    const now = new Date();
    let startDate;
    
    switch (timeRange) {
      case "week":
        startDate = subDays(now, 7);
        break;
      case "month":
        startDate = subDays(now, 30);
        break;
      default:
        return expenses;
    }
    
    return expenses.filter(exp => {
      const expDate = new Date(exp.date);
      return expDate >= startDate && expDate <= now;
    });
  };

  const filteredExpenses = getFilteredExpenses();
  const totalSpent = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const avgDaily = filteredExpenses.length > 0 ? totalSpent / (timeRange === "week" ? 7 : timeRange === "month" ? 30 : Math.max(1, filteredExpenses.length)) : 0;

  // Calculate spending trend data
  const getTrendData = () => {
    const now = new Date();
    const days = timeRange === "week" ? 7 : timeRange === "month" ? 30 : 14;
    const startDate = subDays(now, days - 1);
    
    const dayList = eachDayOfInterval({ start: startDate, end: now });
    
    return dayList.map(day => {
      const dayExpenses = expenses.filter(exp => {
        const expDate = new Date(exp.date);
        return format(expDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
      });
      
      const total = dayExpenses.reduce((sum, exp) => sum + exp.amount, 0);
      
      return {
        date: format(day, timeRange === "week" ? "EEE" : "MMM d"),
        amount: total,
        fullDate: format(day, "MMM d, yyyy")
      };
    });
  };

  // Calculate category breakdown
  const getCategoryData = () => {
    const categoryTotals = {};
    
    filteredExpenses.forEach(exp => {
      const cat = exp.category || "Other";
      categoryTotals[cat] = (categoryTotals[cat] || 0) + exp.amount;
    });
    
    return Object.entries(categoryTotals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  // Calculate spending by day of week
  const getDayOfWeekData = () => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayTotals = days.map(day => ({ day, amount: 0, count: 0 }));
    
    filteredExpenses.forEach(exp => {
      const expDate = new Date(exp.date);
      const dayIndex = expDate.getDay();
      dayTotals[dayIndex].amount += exp.amount;
      dayTotals[dayIndex].count += 1;
    });
    
    // Calculate average per day
    return dayTotals.map(d => ({
      ...d,
      average: d.count > 0 ? d.amount / d.count : 0
    }));
  };

  // Calculate previous period comparison
  const getPreviousPeriodComparison = () => {
    const now = new Date();
    const days = timeRange === "week" ? 7 : 30;
    const currentStart = subDays(now, days);
    const previousStart = subDays(now, days * 2);
    const previousEnd = subDays(now, days + 1);
    
    const currentTotal = expenses
      .filter(exp => new Date(exp.date) >= currentStart)
      .reduce((sum, exp) => sum + exp.amount, 0);
    
    const previousTotal = expenses
      .filter(exp => {
        const d = new Date(exp.date);
        return d >= previousStart && d <= previousEnd;
      })
      .reduce((sum, exp) => sum + exp.amount, 0);
    
    const change = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;
    
    return { currentTotal, previousTotal, change };
  };

  const trendData = getTrendData();
  const categoryData = getCategoryData();
  const dayOfWeekData = getDayOfWeekData();
  const comparison = getPreviousPeriodComparison();
  const estimatedIncome = getIncomeEstimate();
  const savingsRate = estimatedIncome > 0 ? ((estimatedIncome - totalSpent) / estimatedIncome) * 100 : 0;

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-sm">{payload[0].payload.fullDate || label}</p>
          <p className="text-tamba-blue font-semibold">
            ${payload[0].value.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (expenses.length === 0) {
    return (
      <Card className="border-dashed border-2 border-border/50">
        <CardContent className="py-10 text-center">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="font-semibold mb-2">No Data Yet</h3>
          <p className="text-sm text-muted-foreground">
            Start tracking expenses to see spending insights and trends
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with time range selector */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-tamba-blue" />
          Spending Insights
        </h2>
        <div className="flex gap-1 bg-secondary rounded-lg p-1">
          {[
            { value: "week", label: "7D" },
            { value: "month", label: "30D" },
            { value: "all", label: "All" }
          ].map((option) => (
            <Button
              key={option.value}
              variant={timeRange === option.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setTimeRange(option.value)}
              className={`h-7 px-3 text-xs ${timeRange === option.value ? "bg-tamba-blue" : ""}`}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-tamba-blue/20 bg-gradient-to-br from-tamba-blue/5 to-transparent">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Total Spent</p>
              <p className="text-2xl font-bold text-tamba-blue">${totalSpent.toFixed(0)}</p>
              <div className={`flex items-center gap-1 text-xs mt-1 ${comparison.change <= 0 ? "text-green-600" : "text-tamba-coral"}`}>
                {comparison.change <= 0 ? (
                  <ArrowDownRight className="h-3 w-3" />
                ) : (
                  <ArrowUpRight className="h-3 w-3" />
                )}
                <span>{Math.abs(comparison.change).toFixed(0)}% vs prev</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-tamba-teal/20 bg-gradient-to-br from-tamba-teal/5 to-transparent">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Daily Average</p>
              <p className="text-2xl font-bold text-tamba-teal">${avgDaily.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {filteredExpenses.length} transactions
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Savings Rate Card */}
      {income_range && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className={`${savingsRate >= 20 ? "border-green-500/30 bg-green-50/50" : savingsRate >= 0 ? "border-tamba-yellow/30 bg-tamba-yellow/5" : "border-tamba-coral/30 bg-tamba-coral/5"}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Est. Savings Rate</p>
                  <p className={`text-xl font-bold ${savingsRate >= 20 ? "text-green-600" : savingsRate >= 0 ? "text-tamba-yellow" : "text-tamba-coral"}`}>
                    {savingsRate.toFixed(0)}%
                  </p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>Based on {income_range} income</p>
                  <p className={savingsRate >= 20 ? "text-green-600" : "text-tamba-coral"}>
                    {savingsRate >= 20 ? "Great job! 👏" : savingsRate >= 0 ? "Room to improve" : "Over budget ⚠️"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Chart Type Selector */}
      <div className="flex gap-2">
        {[
          { value: "trend", label: "Trend", icon: TrendingUp },
          { value: "category", label: "Categories", icon: PieChartIcon },
          { value: "dayOfWeek", label: "By Day", icon: Calendar }
        ].map((option) => (
          <Button
            key={option.value}
            variant={chartType === option.value ? "default" : "outline"}
            size="sm"
            onClick={() => setChartType(option.value)}
            className={`flex-1 gap-1 ${chartType === option.value ? "bg-tamba-blue" : ""}`}
          >
            <option.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{option.label}</span>
          </Button>
        ))}
      </div>

      {/* Charts */}
      <motion.div
        key={chartType}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardContent className="p-4">
            {chartType === "trend" && (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorAmount)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {chartType === "category" && (
              <div className="h-64 flex items-center">
                <ResponsiveContainer width="50%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`$${value.toFixed(2)}`, 'Amount']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-1/2 pl-4 space-y-2">
                  {categoryData.slice(0, 5).map((cat, index) => (
                    <div key={cat.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="truncate max-w-[80px]">{cat.name}</span>
                      </div>
                      <span className="font-medium">${cat.value.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {chartType === "dayOfWeek" && (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dayOfWeekData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="day" 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`$${value.toFixed(2)}`, name === 'amount' ? 'Total' : 'Avg']}
                    />
                    <Bar 
                      dataKey="amount" 
                      fill="#3B82F6" 
                      radius={[4, 4, 0, 0]}
                      name="Total"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Top Spending Categories */}
      {categoryData.length > 0 && chartType !== "category" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <PieChartIcon className="h-4 w-4 text-tamba-coral" />
                Top Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {categoryData.slice(0, 3).map((cat, index) => {
                  const percentage = (cat.value / totalSpent) * 100;
                  return (
                    <div key={cat.name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{cat.name}</span>
                        <span className="font-medium">${cat.value.toFixed(0)} ({percentage.toFixed(0)}%)</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default SpendingInsights;
